import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import DateRangePicker from "react-bootstrap-daterangepicker";
import { initialSettings } from "../../../core/common/selectoption/selectoption";
import CollapseHeader from "../../../core/common/collapse-header";
import {
  CompanyResponse,
  Contact,
  fetchAllContacts,
} from "../../../api/contact";
import { highlightMatch } from "../../../core/common/highlightmatch";
import PageLoadProgressBar from "../../../core/data/json/pageLoadProgressBar";

const ContactGrid = () => {
  const route = all_routes;
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [allContacts, setAllContacts] = useState<Contact[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortType, setSortType] = useState<
    "asc" | "desc" | "recently_created" | "recently_viewed" | null
  >(null);

  const [dateRange, setDateRange] = useState<{
    start: Date | null;
    end: Date | null;
  }>({
    start: null,
    end: null,
  });
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);

  useEffect(() => {
    const fetchContactsData = async () => {
      try {
        const data = await fetchAllContacts();
        const transformed = transformCompaniesToContacts(data);
        setContacts(transformed);
        setAllContacts(transformed);
      } catch (error) {
        console.error("Error fetching contacts", error);
      }
    };
    fetchContactsData();
  }, []);

  const transformCompaniesToContacts = (
    companies: CompanyResponse[]
  ): Contact[] => {
    return companies.map((item) => ({
      contact_id: item.company.contact_id,
      contact_name: item.company.contact_person_name,
      contact_mail: item.company.company_mail,
      contact_phone: item.company.company_phone,
      contact_description: item.company.contact_remark,
      contact_Company_name: item.company.company_name,
      contact_desigination: item.company.contact_person_desigination,
      lead_from: item.company.leadsource_id,
      client_id: item.company.client_id,
      branches: item.branches,
      address_mode: item.branches.length > 1 ? "multiple" : "single",

      // ✅ Add missing fields used in JSX
      created_at: item.company.created_at,
      status: item.company.status,
      website_url: item.company.website_url,
      username: item.company.username,
      contact_country: item.branches?.[0]?.branch_country || "N/A",
    }));
  };

  const filteredContacts = contacts
    .filter((c) => {
      const q = searchQuery.toLowerCase();
      return (
        c.contact_name?.toLowerCase().includes(q) ||
        c.contact_mail?.toLowerCase().includes(q) ||
        c.contact_Company_name?.toLowerCase().includes(q) ||
        c.contact_phone?.toLowerCase().includes(q) ||
        c.contact_country?.toLowerCase().includes(q)
      );
    })
    .filter((c) => {
      if (!dateRange.start || !dateRange.end || !c.created_at) return true;
      const created = new Date(c.created_at);
      return created >= dateRange.start && created <= dateRange.end;
    })
    .sort((a, b) => {
      if (!sortType) return 0;

      if (sortType === "asc" || sortType === "desc") {
        const nameA = (a.contact_name || "").toLowerCase();
        const nameB = (b.contact_name || "").toLowerCase();
        return sortType === "asc"
          ? nameA.localeCompare(nameB)
          : nameB.localeCompare(nameA);
      }

      if (sortType === "recently_created") {
        return (
          new Date(b.created_at ?? "").getTime() -
          new Date(a.created_at ?? "").getTime()
        );
      }

      if (sortType === "recently_viewed") {
        return 0;
      }

      return 0;
    });

  return (
    <>
      <PageLoadProgressBar />
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              {/* Page Header */}
              <div className="page-header">
                <div className="row align-items-center">
                  <div className="col-8">
                    <h4 className="page-title">
                      <i className="ti ti-user-up text-primary me-2" />
                      Contact
                      <span className="count-title ms-2">
                        {contacts.length}
                      </span>
                    </h4>
                  </div>
                  <div className="col-4 text-end">
                    <div className="head-icons">
                      <CollapseHeader />
                    </div>
                  </div>
                </div>
                <div className="col-sm-6">
                  <ul className="contact-breadcrumb">
                    <li>
                      <Link to={route.contactList}>
                        <i className="ti ti-arrow-narrow-left" />
                        Contacts
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
              {/* /Page Header */}
              <div className="card ">
                <div className="card-header">
                  {/* Search */}
                  <div className="row align-items-center">
                    <div className="col-sm-4">
                      <div className="icon-form mb-3 mb-sm-0">
                        <span className="form-icon">
                          <i className="ti ti-search" />
                        </span>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search Contacts"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                  {/* /Search */}
                </div>

                <div className="card-body">
                  {/* Filter */}
                  <div className="d-flex align-items-center justify-content-between flex-wrap row-gap-2 mb-4">
                    <div className="d-flex align-items-center flex-wrap row-gap-2">
                      <div className="dropdown me-2">
                        <Link
                          to="#"
                          className="dropdown-toggle"
                          data-bs-toggle="dropdown"
                        >
                          <i className="ti ti-sort-ascending-2 me-2" />
                          Sort{" "}
                        </Link>
                        <div className="dropdown-menu  dropdown-menu-start">
                          <ul>
                            <li>
                              <Link
                                to="#"
                                className="dropdown-item"
                                onClick={() => setSortType("asc")}
                              >
                                <i className="ti ti-circle-chevron-right me-1" />
                                Ascending
                              </Link>
                            </li>
                            <li>
                              <Link
                                to="#"
                                className="dropdown-item"
                                onClick={() => setSortType("desc")}
                              >
                                <i className="ti ti-circle-chevron-right me-1" />
                                Descending
                              </Link>
                            </li>

                            <li>
                              <Link
                                to="#"
                                className="dropdown-item"
                                onClick={() => setSortType("recently_created")}
                              >
                                <i className="ti ti-clock-hour-3 me-1" />{" "}
                                Recently Created
                              </Link>
                            </li>
                            <li>
                              <Link
                                to="#"
                                className="dropdown-item"
                                onClick={() => setSortType("recently_viewed")}
                              >
                                <i className="ti ti-eye me-1" /> Recently Viewed
                              </Link>
                            </li>
                          </ul>
                        </div>
                      </div>
                      <div className="icon-form">
                        <span className="form-icon">
                          <i className="ti ti-calendar" />
                        </span>
                        <DateRangePicker
                          initialSettings={initialSettings}
                          onCallback={(start, end) => {
                            setDateRange({
                              start: start.toDate(),
                              end: end.toDate(),
                            });
                          }}
                        >
                          <input
                            className="form-control bookingrange"
                            type="text"
                          />
                        </DateRangePicker>
                      </div>
                    </div>
                    <div className="view-icons">
                      <Link to={route.contactList}>
                        <i className="ti ti-list-tree" />
                      </Link>
                      <Link to="" className="active">
                        <i className="ti ti-grid-dots" />
                      </Link>
                    </div>
                  </div>

                  {/* /Filter */}
                  <>
                    <div className="row">
                      {filteredContacts.map((contact) => (
                        <div
                          className="col-xxl-3 col-xl-4 col-md-6"
                          key={contact.contact_id}
                        >
                          <div className="card border contact-card">
                            <div className="card-body">
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                {contact.status && (
                                  <span
                                    className={`badge ${
                                      contact.status.toLowerCase() === "active"
                                        ? "bg-success"
                                        : contact.status.toLowerCase() ===
                                          "inactive"
                                        ? "bg-danger"
                                        : "bg-warning"
                                    }`}
                                  >
                                    {contact.status}
                                  </span>
                                )}
                              </div>

                              <div className="d-flex align-items-center justify-content-between mb-3">
                                <div className="d-flex align-items-center contact-info">
                                  <Link
                                    to={route.contactDetails}
                                    state={contact}
                                    className="avatar avatar-md flex-shrink-0 me-2"
                                  >
                                    <div className="avatar avatar-md bg-primary text-white d-flex align-items-center justify-content-center rounded-circle">
                                      {contact.contact_name
                                        ?.split(" ")
                                        .map((n) => n[0])
                                        .join("")
                                        .substring(0, 2)
                                        .toUpperCase()}
                                    </div>
                                  </Link>

                                  <div>
                                    <h6
                                      className="text-truncate mb-1 contact-name"
                                      title={`${contact.contact_Company_name} - ${contact.contact_name}`}
                                    >
                                      <Link
                                        to={route.contactDetails}
                                        state={contact}
                                        className="fw-medium text-dark"
                                      >
                                        {highlightMatch(
                                          `${
                                            contact.contact_Company_name ||
                                            "N/A"
                                          } - ${contact.contact_name || "N/A"}`,
                                          searchQuery
                                        )}
                                      </Link>
                                    </h6>
                                    <p
                                      className="text-default text-truncate contact-designation"
                                      title={contact.contact_desigination}
                                    >
                                      {highlightMatch(
                                        contact.contact_desigination || "N/A",
                                        searchQuery
                                      )}
                                    </p>
                                  </div>
                                </div>
                                <div className="dropdown table-action">
                                  <Link
                                    to="#"
                                    className="action-icon"
                                    data-bs-toggle="dropdown"
                                  >
                                    <i className="fa fa-ellipsis-v" />
                                  </Link>
                                  <div className="dropdown-menu dropdown-menu-right">
                                    <Link
                                      className="dropdown-item"
                                      to="#"
                                      onClick={() =>
                                        setSelectedContact(contact)
                                      }
                                    >
                                      <i className="ti ti-eye text-blue-light" />{" "}
                                      Preview
                                    </Link>
                                  </div>
                                </div>
                              </div>

                              <div className="d-block">
                                <div className="d-flex flex-column mb-3">
                                  {/* Email */}
                                  <div className="mb-1 d-flex align-items-center details">
                                    <i className="ti ti-mail text-dark me-2" />
                                    <span className="me-2">
                                      {highlightMatch(
                                        contact.contact_mail || "N/A",
                                        searchQuery
                                      )}
                                    </span>
                                  </div>

                                  {/* Phone */}
                                  <div className="mb-1 d-flex align-items-center details">
                                    <i className="ti ti-phone text-dark me-2" />
                                    <span className="me-2">
                                      {highlightMatch(
                                        contact.contact_phone || "N/A",
                                        searchQuery
                                      )}
                                    </span>
                                  </div>

                                  {/* Country */}
                                  <div className="mb-1 d-flex align-items-center details">
                                    <i className="ti ti-map-pin text-dark me-2" />
                                    <span className="me-2">
                                      {highlightMatch(
                                        contact.contact_country || "N/A",
                                        searchQuery
                                      )}
                                    </span>
                                  </div>

                                  {/* Website */}
                                  <div className="mb-1 d-flex align-items-center details">
                                    <i className="ti ti-globe text-dark me-2" />
                                    <span className="me-2">
                                      {highlightMatch(
                                        contact.website_url || "N/A",
                                        searchQuery
                                      )}
                                    </span>
                                  </div>

                                  {/* Lead Owner */}
                                  <div className="mb-1 d-flex align-items-center details">
                                    <i className="ti ti-user text-dark me-2" />
                                    <span className="fw-semibold me-1">
                                      Lead Owner:
                                    </span>
                                    <span>
                                      {highlightMatch(
                                        contact.username || "N/A",
                                        searchQuery
                                      )}
                                    </span>
                                  </div>

                                  {/* Branch Country */}
                                  <div className="mb-1 d-flex align-items-center details">
                                    <i className="ti ti-network text-dark me-2" />
                                    <span className="fw-semibold me-1">
                                      Country:
                                    </span>
                                    <span>
                                      {highlightMatch(
                                        contact.branches?.[0]?.branch_country ??
                                          "N/A",
                                        searchQuery
                                      )}
                                    </span>
                                  </div>
                                </div>

                                <div className="d-flex align-items-center justify-content-between flex-wrap gap-1">
                                  <span
                                    className="badge badge-tag badge-success-light me-2 text-truncate"
                                    title={contact.contact_Company_name}
                                  >
                                    <i className="fa fa-map-marker-alt text-dark me-1" />
                                    Company:{" "}
                                    {highlightMatch(
                                      contact.contact_Company_name || "N/A",
                                      searchQuery
                                    )}
                                  </span>
                                  <span className="text-default text-end small">
                                    <i className="ti ti-calendar-event me-1" />
                                    {contact.created_at
                                      ? new Date(
                                          contact.created_at
                                        ).toLocaleDateString()
                                      : "N/A"}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {selectedContact && (
                      <div
                        className="modal fade show d-block"
                        tabIndex={-1}
                        style={{ background: "rgba(0,0,0,0.5)" }}
                        onClick={() => setSelectedContact(null)}
                      >
                        <div
                          className="modal-dialog modal-dialog-centered"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="modal-content">
                            <div className="modal-header">
                              <h5 className="modal-title">
                                {selectedContact.contact_name} - Preview
                              </h5>
                              <button
                                type="button"
                                className="btn-close"
                                onClick={() => setSelectedContact(null)}
                              />
                            </div>
                            <div className="modal-body">
                              <p>
                                <strong>Email:</strong>{" "}
                                {selectedContact.contact_mail}
                              </p>
                              <p>
                                <strong>Phone:</strong>{" "}
                                {selectedContact.contact_phone}
                              </p>
                              <p>
                                <strong>Designation:</strong>{" "}
                                {selectedContact.contact_desigination}
                              </p>
                              <p>
                                <strong>Company:</strong>{" "}
                                {selectedContact.contact_Company_name}
                              </p>
                              <p>
                                <strong>Status:</strong>{" "}
                                {selectedContact.status}
                              </p>
                              <p>
                                <strong>Description:</strong>{" "}
                                {selectedContact.contact_description}
                              </p>
                              <p>
                                <strong>Created At:</strong>{" "}
                                {selectedContact.created_at}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="load-btn text-center pb-4">
                      <Link to="#" className="btn btn-primary">
                        Load More Contacts
                        <i className="ti ti-loader" />
                      </Link>
                    </div>
                  </>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
export default ContactGrid;
